import React, { useState, useEffect, useRef } from 'react';

/* ─────────────────────────────────────────────────────────────────────────────
   Dynamicqr.js — matches Home.js design system exactly
   --navy #06112b · --blue #1c73ba · --gold #f5a623 · Clash Display + DM Sans
───────────────────────────────────────────────────────────────────────────── */

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@400,500,600,700&display=swap');

:root {
  --navy:    #06112b;
  --navy2:   #0d1f45;
  --navy3:   #112359;
  --blue:    #1c73ba;
  --blue2:   #2b8de0;
  --blue3:   #5aaef7;
  --gold:    #f5a623;
  --gold2:   #ffc94d;
  --offwhite:#f0f5ff;
  --muted:   #94a3b8;
  --border:  rgba(28,115,186,0.15);
  --surface: rgba(13,31,69,0.6);
}

.dqr-page {
  min-height: 100vh;
  background: linear-gradient(145deg, var(--navy) 0%, var(--navy3) 60%, #1a3570 100%);
  font-family: 'DM Sans', sans-serif;
  color: #fff;
  overflow-x: hidden;
}

/* ── Grid bg ── */
.dqr-page::before {
  content: '';
  position: fixed; inset: 0;
  background-image:
    linear-gradient(rgba(28,115,186,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(28,115,186,0.05) 1px, transparent 1px);
  background-size: 52px 52px;
  pointer-events: none; z-index: 0;
}

/* ── Nav ── */
.dqr-nav {
  position: sticky; top: 0; z-index: 50;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 5%; height: 68px;
  background: rgba(6,17,43,0.9);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border);
}
.dqr-nav-logo {
  font-family: 'Clash Display', sans-serif;
  font-size: 1.5rem; font-weight: 700; color: #fff; text-decoration: none;
}
.dqr-nav-logo span { color: var(--gold); }
.dqr-nav-back {
  display: flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px; padding: 7px 14px;
  color: rgba(255,255,255,0.75); font-size: 0.85rem;
  cursor: pointer; text-decoration: none;
  transition: background 0.2s;
}
.dqr-nav-back:hover { background: rgba(255,255,255,0.12); color: #fff; }

/* ── Hero ── */
.dqr-hero {
  padding: 72px 5% 56px;
  position: relative; z-index: 1;
  display: grid; grid-template-columns: 1fr 1fr;
  align-items: center; gap: 56px;
}
@media (max-width: 860px) { .dqr-hero { grid-template-columns: 1fr; gap: 36px; } }

.dqr-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(245,166,35,0.12);
  border: 1px solid rgba(245,166,35,0.3);
  border-radius: 99px; padding: 5px 14px;
  font-size: 0.72rem; font-weight: 700;
  color: var(--gold); letter-spacing: 2px; text-transform: uppercase;
  margin-bottom: 20px;
}
.dqr-eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--gold); animation: pulseDot 2s infinite;
}
@keyframes pulseDot {
  0%,100% { box-shadow: 0 0 0 0 rgba(245,166,35,0.4); }
  50%      { box-shadow: 0 0 0 5px rgba(245,166,35,0); }
}
.dqr-h1 {
  font-family: 'Clash Display', sans-serif;
  font-size: clamp(2rem, 3.5vw + 0.5rem, 3.2rem);
  font-weight: 700; line-height: 1.1; color: #fff; margin-bottom: 16px;
}
.dqr-h1 em { font-style: normal; color: var(--blue3); }
.dqr-sub {
  font-size: 1rem; color: rgba(255,255,255,0.6);
  line-height: 1.7; max-width: 480px; margin-bottom: 32px;
}

/* ── QR Live Preview ── */
.dqr-preview-wrap {
  display: flex; justify-content: center; align-items: center;
}
.dqr-preview-card {
  background: var(--surface);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(28,115,186,0.25);
  border-radius: 24px;
  padding: 32px;
  display: flex; flex-direction: column; align-items: center; gap: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(28,115,186,0.1);
  position: relative; overflow: hidden;
}
.dqr-preview-card::before {
  content: '';
  position: absolute; top: -40%; left: -40%;
  width: 180%; height: 180%;
  background: radial-gradient(circle at 40% 40%, rgba(28,115,186,0.1) 0%, transparent 60%);
  pointer-events: none;
}
.dqr-qr-img {
  border-radius: 12px;
  border: 3px solid rgba(255,255,255,0.08);
  display: block;
}
.dqr-preview-label {
  font-size: 0.75rem; color: var(--muted); letter-spacing: 1.5px;
  text-transform: uppercase; font-weight: 600;
}
.dqr-url-badge {
  font-size: 0.8rem; color: var(--blue3); background: rgba(28,115,186,0.12);
  border: 1px solid rgba(28,115,186,0.2); border-radius: 8px;
  padding: 4px 12px; max-width: 220px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* ── Section label pattern (matches Home.js) ── */
.dqr-section-label {
  display: block; font-size: 0.72rem; font-weight: 700;
  letter-spacing: 2.5px; text-transform: uppercase;
  color: var(--blue3); margin-bottom: 12px;
}
.dqr-section-heading {
  font-family: 'Clash Display', sans-serif;
  font-size: clamp(1.6rem, 2.5vw + 0.5rem, 2.4rem);
  font-weight: 700; color: #fff; line-height: 1.15; margin-bottom: 8px;
}
.dqr-section-heading em { font-style: normal; color: var(--gold); }

/* ── Customizer section ── */
.dqr-customizer {
  padding: 64px 5%;
  position: relative; z-index: 1;
  background: rgba(6,17,43,0.6);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
}
.dqr-customizer-grid {
  max-width: 1100px; margin: 0 auto;
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 48px; align-items: start;
}
@media (max-width: 860px) { .dqr-customizer-grid { grid-template-columns: 1fr; } }

.dqr-controls { display: flex; flex-direction: column; gap: 20px; }
.dqr-field { display: flex; flex-direction: column; gap: 7px; }
.dqr-field-label {
  font-size: 0.75rem; font-weight: 700; color: var(--muted);
  text-transform: uppercase; letter-spacing: 1.5px;
}
.dqr-input {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(28,115,186,0.2);
  border-radius: 10px; padding: 11px 14px;
  color: #fff; font-size: 0.9rem; font-family: 'DM Sans', sans-serif;
  outline: none; transition: border 0.2s;
  width: 100%; box-sizing: border-box;
}
.dqr-input:focus { border-color: var(--blue2); }
.dqr-row { display: flex; gap: 16px; }
.dqr-color-wrap {
  display: flex; align-items: center; gap: 10px;
}
.dqr-color-swatch {
  width: 40px; height: 40px; border-radius: 10px;
  border: 2px solid rgba(255,255,255,0.12);
  cursor: pointer; overflow: hidden; position: relative;
}
.dqr-color-swatch input[type="color"] {
  position: absolute; inset: 0; opacity: 0;
  width: 100%; height: 100%; cursor: pointer;
}

/* ── Slider ── */
.dqr-slider {
  -webkit-appearance: none; width: 100%; height: 4px;
  background: linear-gradient(90deg, var(--blue), var(--blue2));
  border-radius: 99px; outline: none;
}
.dqr-slider::-webkit-slider-thumb {
  -webkit-appearance: none; width: 18px; height: 18px;
  border-radius: 50%; background: var(--gold);
  cursor: pointer; box-shadow: 0 2px 8px rgba(245,166,35,0.4);
}

/* ── Order card ── */
.dqr-order-card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(28,115,186,0.2);
  border-radius: 20px; padding: 28px;
  backdrop-filter: blur(16px);
  position: relative; overflow: hidden;
}
.dqr-order-card::before {
  content: '';
  position: absolute; top: -30%; right: -30%;
  width: 60%; height: 60%;
  background: radial-gradient(circle, rgba(245,166,35,0.06) 0%, transparent 70%);
  pointer-events: none;
}
.dqr-price {
  font-family: 'Clash Display', sans-serif;
  font-size: 2.4rem; font-weight: 700; color: var(--gold);
  line-height: 1; margin-bottom: 6px;
}
.dqr-price-sub { font-size: 0.8rem; color: var(--muted); margin-bottom: 24px; }

/* Stepper */
.dqr-stepper {
  display: flex; align-items: center; gap: 0;
  background: rgba(255,255,255,0.05); border: 1px solid var(--border);
  border-radius: 10px; overflow: hidden; margin-bottom: 20px; width: fit-content;
}
.dqr-stepper-btn {
  width: 42px; height: 42px; background: transparent; border: none;
  color: var(--blue3); font-size: 1.2rem; cursor: pointer;
  transition: background 0.2s; font-family: 'DM Sans', sans-serif;
}
.dqr-stepper-btn:hover { background: rgba(28,115,186,0.15); }
.dqr-stepper-val {
  width: 52px; text-align: center; background: transparent;
  border: none; border-left: 1px solid var(--border); border-right: 1px solid var(--border);
  color: #fff; font-size: 0.95rem; font-weight: 600; height: 42px;
  font-family: 'DM Sans', sans-serif;
}

/* Buttons */
.dqr-btn-primary {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 13px 24px;
  background: linear-gradient(135deg, var(--blue), var(--blue2));
  color: #fff; border: none; border-radius: 10px;
  font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 700;
  cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 6px 24px rgba(28,115,186,0.4);
  margin-bottom: 10px;
}
.dqr-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(28,115,186,0.5); }
.dqr-btn-secondary {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 12px 24px;
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.8); border: 1px solid rgba(255,255,255,0.14);
  border-radius: 10px; font-family: 'DM Sans', sans-serif;
  font-size: 0.9rem; font-weight: 600; cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}
.dqr-btn-secondary:hover { background: rgba(255,255,255,0.12); transform: translateY(-2px); }
.dqr-btn-gold {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 13px 24px;
  background: linear-gradient(135deg, var(--gold), var(--gold2));
  color: var(--navy); border: none; border-radius: 10px;
  font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 700;
  cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 6px 24px rgba(245,166,35,0.3);
  margin-bottom: 10px;
}
.dqr-btn-gold:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(245,166,35,0.4); }

/* ── Features section ── */
.dqr-features {
  padding: 80px 5%;
  position: relative; z-index: 1;
}
.dqr-features-inner { max-width: 1100px; margin: 0 auto; }
.dqr-features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px; margin-top: 40px;
}
.dqr-feat-card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(28,115,186,0.15);
  border-radius: 18px; padding: 24px 22px;
  transition: border-color 0.3s, transform 0.3s;
  position: relative; overflow: hidden;
}
.dqr-feat-card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, var(--blue), var(--blue3));
  transform: scaleX(0); transform-origin: left;
  transition: transform 0.3s;
}
.dqr-feat-card:hover { border-color: rgba(28,115,186,0.35); transform: translateY(-4px); }
.dqr-feat-card:hover::before { transform: scaleX(1); }
.dqr-feat-icon { font-size: 28px; margin-bottom: 12px; display: block; }
.dqr-feat-title {
  font-family: 'Clash Display', sans-serif;
  font-size: 1rem; font-weight: 600; color: #fff; margin-bottom: 6px;
}
.dqr-feat-desc { font-size: 0.85rem; color: rgba(255,255,255,0.55); line-height: 1.6; }

/* ── Demo modal ── */
.dqr-modal-overlay {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(6,17,43,0.85); backdrop-filter: blur(12px);
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.dqr-modal {
  background: linear-gradient(145deg, var(--navy2), var(--navy3));
  border: 1px solid rgba(28,115,186,0.3);
  border-radius: 24px; padding: 36px; max-width: 440px; width: 100%;
  box-shadow: 0 24px 80px rgba(0,0,0,0.6);
  position: relative;
}
.dqr-modal-close {
  position: absolute; top: 16px; right: 16px;
  width: 32px; height: 32px; border-radius: 50%;
  background: rgba(255,255,255,0.08); border: none;
  color: rgba(255,255,255,0.6); font-size: 16px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s;
}
.dqr-modal-close:hover { background: rgba(255,255,255,0.15); color: #fff; }

/* Demo phone */
.dqr-demo-scene {
  display: flex; align-items: center; justify-content: center;
  gap: 32px; padding: 20px 0;
}
.dqr-demo-qr {
  width: 90px; height: 90px; border-radius: 10px;
  border: 2px solid var(--border); overflow: hidden; flex-shrink: 0;
  transition: transform 0.5s;
}
.dqr-demo-qr.tapping { animation: demoTap 0.5s ease; }
@keyframes demoTap {
  0%   { transform: translateX(0); }
  50%  { transform: translateX(20px) scale(0.95); }
  100% { transform: translateX(0); }
}
.dqr-demo-phone {
  width: 140px; height: 260px;
  background: #0a0a0a; border-radius: 20px;
  border: 2px solid #333; overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 16px 40px rgba(0,0,0,0.6);
  flex-shrink: 0;
}
.dqr-demo-phone-bar {
  height: 28px; background: #111;
  display: flex; align-items: center; justify-content: center;
}
.dqr-demo-phone-notch {
  width: 48px; height: 8px; background: #222; border-radius: 99px;
}
.dqr-demo-screen {
  flex: 1; background: #0d1f45;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 10px; gap: 8px;
}
.dqr-demo-tap-msg {
  font-size: 10px; color: var(--blue3); text-align: center; font-weight: 600;
  animation: fadePulse 1s infinite;
}
@keyframes fadePulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
.dqr-demo-profile {
  display: flex; flex-direction: column; align-items: center; gap: 5px; width: 100%;
}
.dqr-demo-avatar {
  width: 40px; height: 40px; border-radius: 50%;
  background: linear-gradient(135deg, var(--blue), var(--blue3));
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; color: #fff; font-weight: 700;
}
.dqr-demo-name { font-size: 10px; color: #fff; font-weight: 700; }
.dqr-demo-role { font-size: 8px; color: var(--muted); }
.dqr-demo-links {
  display: flex; gap: 5px; flex-wrap: wrap; justify-content: center;
}
.dqr-demo-link-chip {
  font-size: 7px; padding: 2px 6px; border-radius: 99px;
  background: rgba(28,115,186,0.2); color: var(--blue3);
  border: 1px solid rgba(28,115,186,0.3);
}

/* ── Toast ── */
.dqr-toast {
  position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%);
  background: rgba(13,31,69,0.97); color: #fff;
  border: 1px solid var(--border); border-radius: 10px;
  padding: 12px 22px; font-size: 0.85rem; font-weight: 600;
  z-index: 300; box-shadow: 0 8px 32px rgba(0,0,0,0.4);
  animation: slideUp 0.3s ease;
}
@keyframes slideUp { from { transform: translateX(-50%) translateY(10px); opacity:0; } }

/* ── Download btn ── */
.dqr-download-btn {
  display: flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,0.05); border: 1px solid var(--border);
  border-radius: 8px; padding: 8px 14px;
  color: rgba(255,255,255,0.7); font-size: 0.82rem; font-weight: 600;
  cursor: pointer; transition: all 0.2s; margin-top: 10px;
  width: 100%; justify-content: center;
}
.dqr-download-btn:hover { background: rgba(28,115,186,0.15); color: var(--blue3); border-color: var(--blue); }
`;

// ── QR generator (uses qrcode npm or canvas fallback) ─────────────────────
function QRCanvas({ text, size, fgColor, bgColor }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState('');

  useEffect(() => {
    if (!text) { setDataUrl(''); return; }
    // Try npm qrcode first
    if (window.QRCode) {
      window.QRCode.toDataURL(text, { width: size, margin: 2, color: { dark: fgColor, light: bgColor } })
        .then(url => setDataUrl(url)).catch(() => setDataUrl(''));
      return;
    }
    // Fallback: load from CDN
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
    s.onload = () => {
      try {
        const div = document.createElement('div');
        new window.QRCode(div, { text, width: size, height: size, colorDark: fgColor, colorLight: bgColor });
        setTimeout(() => {
          const img = div.querySelector('img');
          if (img) setDataUrl(img.src);
          else { const c = div.querySelector('canvas'); if (c) setDataUrl(c.toDataURL()); }
        }, 200);
      } catch { setDataUrl(''); }
    };
    document.head.appendChild(s);
  }, [text, size, fgColor, bgColor]);

  if (!dataUrl) return (
    <div style={{ width: size, height: size, background: bgColor, borderRadius: 12,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#999', fontSize: 12, border: '1px dashed rgba(255,255,255,0.1)' }}>
      Generating…
    </div>
  );
  return <img ref={canvasRef} src={dataUrl} alt="QR Code" width={size} height={size}
    style={{ borderRadius: 12, display: 'block' }} className="dqr-qr-img" />;
}

const FEATURES = [
  { icon: '⚡', title: 'Instant Generation', desc: 'Create QR codes in real-time as you type. No page reloads needed.' },
  { icon: '🎨', title: 'Fully Customizable', desc: 'Choose any foreground and background color, size from 100–512px.' },
  { icon: '📲', title: 'NFC + QR Dual Mode', desc: 'Pair with a Flap NFC card for tap and scan in one accessory.' },
  { icon: '🔄', title: 'Dynamic Content', desc: 'Update the URL behind your QR anytime without reprinting.' },
  { icon: '📥', title: 'Download Ready', desc: 'Export your QR as PNG for print, digital, or web use.' },
  { icon: '🔒', title: 'Secure Short Links', desc: 'Powered by Flap short-link system with analytics tracking.' },
];

export default function Dynamicqr() {
  const [text,    setText]    = useState('https://flap.com.np');
  const [size,    setSize]    = useState(200);
  const [fgColor, setFgColor] = useState('#06112b');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [qty,     setQty]     = useState(1);
  const [toast,   setToast]   = useState('');
  const [demo,    setDemo]    = useState(false);
  const [demoStep,setDemoStep]= useState(0);
  const [qrDataUrl, setQrDataUrl] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2800); };

  const handleOrder = () => {
    showToast('Opening WhatsApp…');
    const msg = `Hi! I'd like to order ${qty} Dynamic QR unit(s) at Rs. 999 each. Total: Rs. ${qty * 999}.`;
    setTimeout(() => window.open(`https://wa.me/9779802365432?text=${encodeURIComponent(msg)}`, '_blank'), 900);
  };

  const handleDemo = () => {
    setDemo(true); setDemoStep(1);
    setTimeout(() => setDemoStep(2), 900);
    setTimeout(() => setDemoStep(3), 1800);
  };

  const handleDownload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => { ctx.drawImage(img, 0, 0); canvas.toBlob(b => { const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'flap-qr.png'; a.click(); }); };
    img.src = document.querySelector('.dqr-qr-img')?.src || '';
    if (!img.src) showToast('QR not ready yet — try again in a moment.');
  };

  return (
    <div className="dqr-page">
      <style>{STYLES}</style>

      {/* Nav */}
      <nav className="dqr-nav">
        <a href="/" className="dqr-nav-logo">flap<span>.</span></a>
        <a href="/" className="dqr-nav-back">← Back to home</a>
      </nav>

      {/* Hero */}
      <section className="dqr-hero" style={{ maxWidth: 1200, margin: '0 auto', boxSizing: 'border-box' }}>
        <div>
          <div className="dqr-eyebrow">
            <span className="dqr-eyebrow-dot"/>NFC Product · Rs. 999
          </div>
          <h1 className="dqr-h1">Dynamic <em>QR Code</em></h1>
          <p className="dqr-sub">
            Smart QR codes that update in real-time. Change the destination without reprinting —
            paired with Flap NFC for the ultimate tap-and-scan experience.
          </p>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            <button className="dqr-btn-gold" style={{ width:'auto', padding:'11px 28px' }} onClick={handleOrder}>
              Order Now — Rs. 999
            </button>
            <button className="dqr-btn-secondary" style={{ width:'auto', padding:'11px 28px' }} onClick={handleDemo}>
              ▶ See Demo
            </button>
          </div>
        </div>

        {/* Live QR preview */}
        <div className="dqr-preview-wrap">
          <div className="dqr-preview-card">
            <QRCanvas text={text || 'https://flap.com.np'} size={size} fgColor={fgColor} bgColor={bgColor}/>
            <span className="dqr-preview-label">Live Preview</span>
            <span className="dqr-url-badge">{text || 'https://flap.com.np'}</span>
          </div>
        </div>
      </section>

      {/* Customizer */}
      <section className="dqr-customizer">
        <div className="dqr-customizer-grid">
          {/* Controls */}
          <div>
            <span className="dqr-section-label">Customizer</span>
            <h2 className="dqr-section-heading">Make it <em>yours</em></h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize:'0.9rem', marginBottom:28 }}>
              Every change updates the preview instantly on the right.
            </p>
            <div className="dqr-controls">
              <div className="dqr-field">
                <label className="dqr-field-label">URL / Content</label>
                <input className="dqr-input" value={text} onChange={e=>setText(e.target.value)}
                  placeholder="https://yourprofile.flap.com.np" />
              </div>
              <div className="dqr-field">
                <label className="dqr-field-label">Size — {size}px</label>
                <input type="range" className="dqr-slider" min="100" max="400" step="10"
                  value={size} onChange={e=>setSize(+e.target.value)}/>
              </div>
              <div className="dqr-row">
                <div className="dqr-field" style={{ flex:1 }}>
                  <label className="dqr-field-label">QR Color</label>
                  <div className="dqr-color-wrap">
                    <div className="dqr-color-swatch" style={{ background: fgColor }}>
                      <input type="color" value={fgColor} onChange={e=>setFgColor(e.target.value)}/>
                    </div>
                    <span style={{ color:fgColor, fontSize:'0.85rem', fontWeight:600 }}>{fgColor}</span>
                  </div>
                </div>
                <div className="dqr-field" style={{ flex:1 }}>
                  <label className="dqr-field-label">Background</label>
                  <div className="dqr-color-wrap">
                    <div className="dqr-color-swatch" style={{ background: bgColor }}>
                      <input type="color" value={bgColor} onChange={e=>setBgColor(e.target.value)}/>
                    </div>
                    <span style={{ color:'rgba(255,255,255,0.6)', fontSize:'0.85rem', fontWeight:600 }}>{bgColor}</span>
                  </div>
                </div>
              </div>
              <button className="dqr-download-btn" onClick={handleDownload}>📥 Download QR as PNG</button>
            </div>
          </div>

          {/* Order card */}
          <div className="dqr-order-card">
            <span className="dqr-section-label">Order</span>
            <div className="dqr-price">Rs. 999</div>
            <div className="dqr-price-sub">per unit · free shipping within Kathmandu</div>

            <label className="dqr-field-label" style={{ marginBottom:8, display:'block' }}>Quantity</label>
            <div className="dqr-stepper">
              <button className="dqr-stepper-btn" onClick={()=>setQty(q=>Math.max(1,q-1))}>−</button>
              <input className="dqr-stepper-val" type="number" value={qty} min="1"
                onChange={e=>setQty(Math.max(1,+e.target.value))}/>
              <button className="dqr-stepper-btn" onClick={()=>setQty(q=>q+1)}>+</button>
            </div>
            <div style={{ fontSize:'0.8rem', color:'var(--muted)', marginBottom:20 }}>
              Total: <strong style={{color:'var(--gold)'}}>Rs. {qty * 999}</strong>
            </div>

            <button className="dqr-btn-gold" onClick={handleOrder}>
              📱 Order via WhatsApp
            </button>
            <button className="dqr-btn-secondary" onClick={handleDemo}>
              ▶ View Demo
            </button>

            {/* What's included */}
            <div style={{ marginTop:24, paddingTop:20, borderTop:'1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ fontSize:'0.72rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:12 }}>
                What's included
              </div>
              {['Dynamic QR sticker/card','Flap dashboard access','Analytics tracking','Lifetime URL updates'].map(i=>(
                <div key={i} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
                  <span style={{ color:'var(--gold)', fontSize:12 }}>✓</span>
                  <span style={{ fontSize:'0.85rem', color:'rgba(255,255,255,0.65)' }}>{i}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="dqr-features">
        <div className="dqr-features-inner">
          <span className="dqr-section-label">Why Dynamic QR</span>
          <h2 className="dqr-section-heading">More than a <em>QR code</em></h2>
          <div className="dqr-features-grid">
            {FEATURES.map(f => (
              <div className="dqr-feat-card" key={f.title}>
                <span className="dqr-feat-icon">{f.icon}</span>
                <div className="dqr-feat-title">{f.title}</div>
                <div className="dqr-feat-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo modal */}
      {demo && (
        <div className="dqr-modal-overlay" onClick={e=>{ if(e.target===e.currentTarget){ setDemo(false); setDemoStep(0); }}}>
          <div className="dqr-modal">
            <button className="dqr-modal-close" onClick={()=>{setDemo(false);setDemoStep(0);}}>✕</button>
            <h3 style={{ fontFamily:"'Clash Display',sans-serif", fontSize:'1.3rem', fontWeight:700, color:'#fff', marginBottom:6 }}>
              How it works
            </h3>
            <p style={{ fontSize:'0.82rem', color:'var(--muted)', marginBottom:20 }}>
              Hold your QR near any phone camera — no app needed.
            </p>
            <div className="dqr-demo-scene">
              {/* QR card */}
              <div className={`dqr-demo-qr ${demoStep >= 2 ? 'tapping' : ''}`}>
                <QRCanvas text={text || 'https://flap.com.np'} size={90} fgColor={fgColor} bgColor={bgColor}/>
              </div>
              {/* Arrow */}
              <div style={{ fontSize:20, color:'var(--blue3)', animation: demoStep>=2 ? 'fadePulse 0.8s 3' : 'none' }}>→</div>
              {/* Phone */}
              <div className="dqr-demo-phone">
                <div className="dqr-demo-phone-bar"><div className="dqr-demo-phone-notch"/></div>
                <div className="dqr-demo-screen">
                  {demoStep === 1 && <div className="dqr-demo-tap-msg">📷 Point camera at QR…</div>}
                  {demoStep === 2 && <div className="dqr-demo-tap-msg">✅ QR Detected!</div>}
                  {demoStep >= 3 && (
                    <div className="dqr-demo-profile">
                      <div className="dqr-demo-avatar">F</div>
                      <div className="dqr-demo-name">Flap Profile</div>
                      <div className="dqr-demo-role">flap.com.np/yourname</div>
                      <div className="dqr-demo-links">
                        {['LinkedIn','WhatsApp','Website'].map(l=>(
                          <span key={l} className="dqr-demo-link-chip">{l}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {demoStep < 3 && (
              <button className="dqr-btn-primary" style={{ marginTop:16 }} onClick={handleDemo}>
                ▶ Restart Demo
              </button>
            )}
            {demoStep >= 3 && (
              <button className="dqr-btn-gold" style={{ marginTop:16 }} onClick={handleOrder}>
                Order Now — Rs. 999
              </button>
            )}
          </div>
        </div>
      )}

      {toast && <div className="dqr-toast">{toast}</div>}
    </div>
  );
}